import { FC, useMemo } from "react";
import SourceDataItemValue, { ISourceDataItemValueProps } from "./SourceDataItemValue";
import useCtcae, { ICtcaeIdentifier } from "@/hooks/data/useCtcae";
import { Box, CircularProgress, Divider, Tooltip, Typography, alpha, styled, useTheme, lighten } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppSettings } from "@/AppSettings";
import { faPerson, faUser } from "@fortawesome/pro-solid-svg-icons";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { AppSettings } from "@/AppSettings";

const CtcaeTooltip = styled(Box)(({ theme }) => ({
    margin: theme.spacing( -1),
}));

const CtcaeTooltipBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(0, -1),
    padding: theme.spacing(2, 3, 2, 3)
}));

const SourceDataItemCtcaeValue: FC<ISourceDataItemValueProps> = ({
    itemData,
    hideBorderStyles,
    hideStyles
}) => {
    const ctcaeIdentifier = useMemo(() => {
        if (!itemData?.value) {
            return;
        }

        return JSON.parse(itemData.value) as ICtcaeIdentifier;
    }, [itemData?.value]);
    
    const { data: ctcae, loading: ctcaeLoading } = useCtcae(ctcaeIdentifier); 

    const theme = useTheme();
    return (
        <SourceDataItemValue
            hideBorderStyles={hideBorderStyles}
            hideStyles={hideStyles}
            startAdornment={
                ctcaeLoading ? 
                    <CircularProgress size="16px" /> :
                    ctcae?.grade ?
                        <Box 
                            sx={{
                                opacity:  
                                    ctcae.grade.name === '5' ?
                                        1 :
                                        ctcae.grade.name === '4' ?
                                            0.75 :
                                            ctcae.grade.name === '3' ?
                                                0.5 :
                                                ctcae.grade.name === '2' ?
                                                    0.25 :
                                                    0.1
                            }}
                        >
                            <FontAwesomeIcon icon={faUser} />
                        </Box>:
                        undefined
            }
            endAdornment={
                ctcae && (
                    <Tooltip 
                        placement="bottom-end"
                        title={
                            <CtcaeTooltip>
                                <CtcaeTooltipBox>
                                    <Typography variant="subtitle1">
                                        Version
                                    </Typography>
                                    <Typography variant="caption">
                                        {ctcae.version?.name}
                                    </Typography>
                                </CtcaeTooltipBox>
                                <Divider />
                                <CtcaeTooltipBox>
                                    <Typography variant="subtitle1">
                                        Category
                                    </Typography>
                                    <Typography variant="caption">
                                        {ctcae.category?.name}
                                    </Typography>
                                </CtcaeTooltipBox>
                                {
                                    ctcae.parentTerm && (
                                        <>
                                            <Divider />
                                            <CtcaeTooltipBox>
                                                <Typography variant="subtitle1">
                                                    {ctcae.parentTerm.name}
                                                </Typography>
                                                <Typography variant="caption" component="div">
                                                    MedDRA Code: <em>{ctcae.parentTerm.medDraCode}</em>
                                                </Typography>
                                                {
                                                    ctcae.parentTerm.specifyOther && (
                                                        <Typography variant="caption" component="div">
                                                            {ctcae.specification}
                                                        </Typography>
                                                    )
                                                }
                                                <Typography variant="caption" component="div">
                                                    {ctcae.parentTerm.description}
                                                </Typography>
                                            </CtcaeTooltipBox>
                                        </>
                                    )
                                }
                                <Divider />
                                <CtcaeTooltipBox>
                                    <Typography variant="subtitle1">
                                        {ctcae.term?.name.replace(ctcae.category.name + ' - ', '')}
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                        MedDRA Code: <em>{ctcae.term.medDraCode}</em>
                                    </Typography>
                                    {
                                        ctcae.term?.specifyOther && (
                                            <Typography variant="caption" component="div">
                                                {ctcae.specification}
                                            </Typography>
                                        )
                                    }
                                    <Typography variant="caption" component="div">
                                        {ctcae.term?.description}
                                    </Typography>
                                </CtcaeTooltipBox>
                                {
                                    ctcae.grade && (
                                        <>
                                            <Divider />
                                            <CtcaeTooltipBox>
                                                <Typography variant="subtitle1">
                                                    Grade {ctcae.grade.name}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {ctcae.grade.description}
                                                </Typography>
                                            </CtcaeTooltipBox>
                                        </>
                                    )
                                }
                            </CtcaeTooltip>
                        }
                    >
                        <FontAwesomeIcon size="lg" icon={AppSettings.icons.alertInformation} />
                    </Tooltip>
                )
            }
        >
            {
                ctcae && ctcaeIdentifier?.staticTerm && (
                    ctcae.grade ? `Grade ${ctcae.grade?.name}` : ' '
                )
            }
            {
                ctcae && !ctcaeIdentifier?.staticTerm && (
                    <>
                        {ctcae.term.name}{ctcae.specification ? ` (${ctcae.specification})` : null}{ctcae.grade ? `: Grade ${ctcae.grade?.name}` : null}
                    </>
                )
            }
        </SourceDataItemValue>
    );
};

export default SourceDataItemCtcaeValue;